import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../lib/auth';
import { dashboardAPI, bookingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Package,
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  ArrowRight,
  FileText,
  AlertCircle
} from 'lucide-react';

const DashboardPage = () => {
  const { user, isAdmin, isAgent } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    // Check if agent needs redirect
    if (isAgent()) {
      setShouldRedirect(true);
    } else {
      loadStats();
    }
  }, [isAgent]);

  // Redirect agents to profile page
  if (shouldRedirect) {
    return <Navigate to="/profile" replace />;
  }

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
    };
    return <Badge variant="outline" className={variants[status] || variants.pending}>{status}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-6 animate-pulse">
              <div className="w-10 h-10 bg-slate-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name}!</h2>
          <p className="text-white/80">{isAdmin() ? 'Manage your travel business from here.' : 'Track your bookings and explore packages.'}</p>
        </div>

        {/* Stats Grid */}
        {isAdmin() ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="shadow-[0_2px_10px_-4px_rgba(79,70,229,0.1)]" data-testid="stat-packages">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-4">
                  <Package size={24} />
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Active Packages</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_packages || 0}</p>
              </CardContent>
            </Card>

            <Card className="shadow-[0_2px_10px_-4px_rgba(79,70,229,0.1)]" data-testid="stat-bookings">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-4">
                  <Calendar size={24} />
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Bookings</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_bookings || 0}</p>
                {stats?.pending_bookings > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{stats.pending_bookings} pending</p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-[0_2px_10px_-4px_rgba(79,70,229,0.1)]" data-testid="stat-agents">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  <Users size={24} />
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Verified Agents</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_agents || 0}</p>
                {stats?.pending_agents > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{stats.pending_agents} pending approval</p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-[0_2px_10px_-4px_rgba(79,70,229,0.1)]" data-testid="stat-revenue">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp size={24} />
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Revenue</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">₹{(stats?.total_revenue || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-[0_2px_10px_-4px_rgba(79,70,229,0.1)]">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-4">
                  <Calendar size={24} />
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">My Bookings</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_bookings || 0}</p>
              </CardContent>
            </Card>

            <Card className="shadow-[0_2px_10px_-4px_rgba(79,70,229,0.1)]">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp size={24} />
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Confirmed Bookings</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.confirmed_bookings || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        {isAdmin() && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button asChild variant="outline" className="h-auto p-4 justify-start" data-testid="quick-packages">
              <Link to="/admin/packages" className="flex items-center gap-3">
                <Package size={20} className="text-primary" />
                <div className="text-left">
                  <p className="font-semibold">Manage Packages</p>
                  <p className="text-xs text-muted-foreground">Add or edit packages</p>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-auto p-4 justify-start" data-testid="quick-bookings">
              <Link to="/admin/bookings" className="flex items-center gap-3">
                <Calendar size={20} className="text-amber-600" />
                <div className="text-left">
                  <p className="font-semibold">View Bookings</p>
                  <p className="text-xs text-muted-foreground">Manage requests</p>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-auto p-4 justify-start" data-testid="quick-agents">
              <Link to="/admin/agents" className="flex items-center gap-3">
                <Users size={20} className="text-emerald-600" />
                <div className="text-left">
                  <p className="font-semibold">Agent Approvals</p>
                  <p className="text-xs text-muted-foreground">Review registrations</p>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-auto p-4 justify-start" data-testid="quick-reports">
              <Link to="/admin/reports" className="flex items-center gap-3">
                <FileText size={20} className="text-rose-600" />
                <div className="text-left">
                  <p className="font-semibold">Monthly Reports</p>
                  <p className="text-xs text-muted-foreground">Export & analyze</p>
                </div>
              </Link>
            </Button>
          </div>
        )}

        {/* Recent Bookings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Bookings</CardTitle>
                <CardDescription>Latest booking requests</CardDescription>
              </div>
              {isAdmin() && (
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin/bookings">View All <ArrowRight size={14} className="ml-1" /></Link>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_bookings?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_bookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-testid={`recent-booking-${booking.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Calendar size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{booking.guest_name}</p>
                        <p className="text-sm text-slate-500">{booking.package_title}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">₹{booking.total_amount?.toLocaleString()}</p>
                      {getStatusBadge(booking.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar size={40} className="mx-auto mb-4 text-slate-300" />
                <p>No recent bookings</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
